/**
 * BroadcastSchedule — transmission planning. The readiness gate is enforced
 * on the server; a TV director can override it deliberately.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { CalendarClock, Trash2 } from 'lucide-react';
import api, { getErrorMessage } from '@/lib/api';

export const BroadcastSchedule = () => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    episode_id: '', broadcast_date: '', start_time: '18:00', kind: 'premiere', force: false,
  });

  const { data } = useQuery({
    queryKey: ['fwtv-broadcasts'],
    queryFn: () => api.get('/fwtv/studio/broadcasts').then((r) => r.data),
  });
  const { data: episodes } = useQuery({
    queryKey: ['fwtv-episodes'],
    queryFn: () => api.get('/fwtv/studio/episodes').then((r) => r.data),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['fwtv-broadcasts'] });
    qc.invalidateQueries({ queryKey: ['fwtv-studio-overview'] });
  };

  const schedule = useMutation({
    mutationFn: () => api.post('/fwtv/studio/broadcasts', form),
    onSuccess: () => { toast.success('Broadcast scheduled'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not schedule that broadcast')),
  });

  const remove = useMutation({
    mutationFn: (id) => api.delete(`/fwtv/studio/broadcasts/${id}`),
    onSuccess: () => { toast.success('Broadcast removed'); refresh(); },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not remove that broadcast')),
  });

  const rows = data?.broadcasts || [];
  const epList = episodes?.episodes || [];
  const chosen = epList.find((e) => e.id === form.episode_id);
  const blocked = chosen && !chosen.readiness?.ready_to_schedule;

  return (
    <div className="space-y-5" data-testid="fwtv-broadcast-schedule">
      <Card>
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div className="md:col-span-2">
            <Label>Episode</Label>
            <Select value={form.episode_id} onValueChange={(v) => setForm({ ...form, episode_id: v })}>
              <SelectTrigger data-testid="broadcast-episode"><SelectValue placeholder="Choose an episode" /></SelectTrigger>
              <SelectContent>
                {epList.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.title}{e.readiness?.ready_to_schedule ? '' : ' · not ready'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Broadcast date</Label>
            <Input type="date" value={form.broadcast_date} data-testid="broadcast-date"
                   onChange={(e) => setForm({ ...form, broadcast_date: e.target.value })} />
          </div>
          <div>
            <Label>Start time</Label>
            <Input type="time" value={form.start_time} data-testid="broadcast-time"
                   onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div>
            <Label>Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="broadcast-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="premiere">Premiere</SelectItem>
                <SelectItem value="live">Live</SelectItem>
                <SelectItem value="repeat">Repeat</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-5 flex flex-wrap items-center gap-4">
            <Button onClick={() => schedule.mutate()} data-testid="broadcast-schedule-btn"
                    disabled={!form.broadcast_date || schedule.isPending}>
              <CalendarClock className="h-4 w-4 mr-1" /> Schedule broadcast
            </Button>
            {blocked && (
              <label className="flex items-center gap-2 text-xs text-amber-700">
                <Checkbox checked={form.force} data-testid="broadcast-force"
                          onCheckedChange={(v) => setForm({ ...form, force: !!v })} />
                Override the readiness gate (TV director only)
              </label>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="border rounded-md divide-y" data-testid="broadcast-list">
        {rows.length === 0 && (
          <p className="p-6 text-sm text-slate-500" data-testid="broadcast-empty">
            No broadcasts scheduled.
          </p>
        )}
        {rows.map((b) => (
          <div key={b.id} className="flex flex-wrap items-center gap-4 px-4 py-3 text-sm"
               data-testid={`broadcast-${b.id}`}>
            <span className="font-mono text-xs">{new Date(b.broadcast_at).toLocaleString()}</span>
            <span className="flex-1 min-w-0 truncate">
              {b.episode_title || b.programme_title || 'Unassigned'}
            </span>
            <Badge variant="outline">{b.kind}</Badge>
            {b.readiness && (
              <Badge className={b.readiness.ready_to_schedule
                ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100'
                : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                {b.readiness.ready_to_schedule ? 'Ready' : 'Checks outstanding'}
              </Badge>
            )}
            {b.forced && <Badge variant="outline" className="text-amber-700">Overridden</Badge>}
            <Button variant="ghost" size="icon" onClick={() => remove.mutate(b.id)}
                    data-testid={`broadcast-remove-${b.id}`}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BroadcastSchedule;
